import { Component, Fragment, Suspense, useDeferredValue, useState, type ReactNode } from 'react'
import { createFileRoute, stripSearchParams } from '@tanstack/react-router'
import { noop, useMutation, useSuspenseQuery } from '@tanstack/react-query'
import type { inferRouterOutputs } from '@trpc/server'
import type { AppRouter } from 'server/trpc'
import * as valibot from 'valibot'
import { ERROR_STATUSES, type ErrorStatus } from 'shared/error-status'
import { ChevronDownIcon } from 'lucide-react'
import { trpc } from '@/main'
import { toast } from '@/lib/toast'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { SearchInput } from '@/components/ui/search-input'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { ButtonPrimitive } from '@/components/ui/button-primitive'

// The captured-errors surface, modelled on medicos' Sentry-style client-errors tab: a table of deduped
// issues, each expandable to its latest occurrence's stack, breadcrumb trail, and the FULL wide-event
// attribute bag (the same OTel field set errors.service tees onto the row). Defaults to `active`; there's
// no "all" view — you triage from one status at a time.

/** The status the panel lands on when the URL doesn't name one. */
const DEFAULT_ERROR_STATUS: ErrorStatus = 'active'

export const Route = createFileRoute('/_app/dev-resources/errors')({
  // The triage status is a SEARCH param, not component state: it is the query's key, and `loaderDeps` is
  // the only thing that re-runs a route's `context`. Keeping it in the URL means the loader and the panel
  // read the one object below — no second, filtered key built at render — and a triage view is
  // linkable and survives back/forward.
  validateSearch: valibot.object({
    errorStatus: valibot.fallback(
      valibot.optional(valibot.picklist(ERROR_STATUSES), DEFAULT_ERROR_STATUS),
      DEFAULT_ERROR_STATUS,
    ),
  }),
  // Keep the default out of the URL, the way the root strips its own defaults — `/errors` stays clean
  // and only a non-default triage view carries a param.
  search: { middlewares: [stripSearchParams({ errorStatus: DEFAULT_ERROR_STATUS })] },
  loaderDeps: ({ search }) => ({ errorStatus: search.errorStatus }),
  context: ({ deps }) => ({
    errorsQueryOptions: trpc.admin.listErrors.queryOptions({ status: deps.errorStatus }),
  }),
  loader: ({ context }) => {
    context.queryClient.query(context.errorsQueryOptions).catch(noop)
  },
  component: RouteComponent,
})

type StoredError = inferRouterOutputs<AppRouter>['admin']['listErrors'][number]
type ErrorCtx = NonNullable<StoredError['context']>

const label = (status: ErrorStatus): string => status.charAt(0).toUpperCase() + status.slice(1)

// Effective-status → Badge variant. `active` (incl. regressions) is the one that demands attention.
const STATUS_VARIANT: Record<ErrorStatus, 'destructive' | 'secondary' | 'outline'> = {
  active: 'destructive',
  ignored: 'outline',
  resolved: 'secondary',
}

const fmt = (date: string | Date): string => new Date(date).toLocaleString()

// Stringify an attribute value for display; null/undefined (and non-scalars) → null so the row is dropped.
const attrStr = (v: unknown): string | null =>
  v == null ? null : typeof v === 'string' ? v : typeof v === 'number' || typeof v === 'boolean' ? String(v) : null

function RouteComponent() {
  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-xl font-semibold">Captured errors</h2>
        <p className="text-muted-foreground text-sm">
          Server 500s and uncaught client errors, deduped by fingerprint. The first production occurrence of a new error
          auto-opens a GitHub issue (picked up by the <code className="text-xs">claude-issue-fix</code> workflow).
          Triage each as active / ignored / resolved.
        </p>
      </div>
      <ErrorsPanelBoundary>
        <Suspense fallback={<ErrorsPanelSkeleton />}>
          <ErrorsPanel />
        </Suspense>
      </ErrorsPanelBoundary>
    </section>
  )
}

function ErrorsPanel() {
  const { errorStatus: filter } = Route.useSearch()
  const navigate = Route.useNavigate()
  const context = Route.useRouteContext()
  const [search, setSearch] = useState('')
  // Defer the route's OPTIONS object (reference-stable per match) so a tab switch keeps the previous
  // status's rows on screen while the new ones load, instead of flashing the suspense skeleton: React
  // holds the last committed tree through the deferred re-suspense.
  const deferredErrors = useDeferredValue(context.errorsQueryOptions)
  const { data: errors } = useSuspenseQuery(deferredErrors)

  const setStatus = useMutation(
    trpc.admin.setErrorStatus.mutationOptions({
      onError: () => toast.error('Couldn’t update the error status.'),
    }),
  )

  const term = search.trim().toLowerCase()
  const rows = term
    ? errors.filter((e) => `${e.errorType} ${e.message} ${e.route}`.toLowerCase().includes(term))
    : errors

  const occurrences = errors.reduce((n, e) => n + e.count, 0)
  const serverCount = errors.filter((e) => e.origin === 'server').length

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="bg-card inline-flex items-center gap-1 rounded-lg border p-1">
          {ERROR_STATUSES.map((status) => (
            <ButtonPrimitive
              key={status}
              type="button"
              onClick={() => void navigate({ search: (prev) => ({ ...prev, errorStatus: status }), replace: true })}
              className={cn(
                'rounded-md px-3 py-1 text-xs font-medium transition-colors',
                filter === status ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted',
              )}
            >
              {label(status)}
            </ButtonPrimitive>
          ))}
        </div>
        <div className="max-w-xs flex-1">
          <SearchInput value={search} onChange={setSearch} placeholder="Type, message or route…" className="h-9" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Issues" value={errors.length} />
        <Stat label="Occurrences" value={occurrences} />
        <Stat label="Server" value={serverCount} />
        <Stat label="Client" value={errors.length - serverCount} />
      </div>

      {rows.length === 0 ? (
        <p className="text-muted-foreground rounded-xl border p-6 text-sm">
          {term ? 'No errors match your search.' : `No ${filter} errors captured. 🎉`}
        </p>
      ) : (
        <div className="w-full overflow-hidden rounded-xl border">
          <Table className="table-fixed border-collapse">
            <TableHeader className="bg-muted/50">
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-muted-foreground w-[44%] truncate px-3 py-2">Error</TableHead>
                <TableHead className="text-muted-foreground w-[16%] truncate px-3 py-2">Route</TableHead>
                <TableHead className="text-muted-foreground w-[8%] truncate px-3 py-2">Events</TableHead>
                <TableHead className="text-muted-foreground w-[16%] truncate px-3 py-2">Last seen</TableHead>
                <TableHead className="text-muted-foreground w-[16%] truncate px-3 py-2">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((e) => (
                <ErrorRow key={e.fingerprint} error={e} onSetStatus={setStatus.mutate} pending={setStatus.isPending} />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}

function Stat(props: { label: string; value: number }) {
  return (
    <div className="bg-card flex flex-col gap-1 rounded-lg border p-3">
      <span className="text-muted-foreground text-xs">{props.label}</span>
      <span className="text-2xl font-semibold tabular-nums">{props.value}</span>
    </div>
  )
}

// A body cell: top-aligned and wrapping (long messages/routes), unlike TableCell's middle/nowrap default.
const cellClass = 'px-3 py-2 align-top break-words whitespace-normal'

function ErrorRow(props: {
  error: StoredError
  onSetStatus: (input: { fingerprint: string; status: ErrorStatus }) => void
  pending: boolean
}) {
  const error = props.error
  const [open, setOpen] = useState(false)
  const regressed = error.effectiveStatus !== error.status
  return (
    <Fragment>
      <TableRow className="hover:bg-muted/30 align-top">
        <TableCell className={cellClass}>
          <ButtonPrimitive
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            className="flex w-full items-start gap-2 text-left"
          >
            <ChevronDownIcon
              className={cn(
                'text-muted-foreground mt-0.5 size-3.5 shrink-0 transition-transform',
                open && 'rotate-180',
              )}
            />
            <span className="flex min-w-0 flex-col gap-1">
              <span className="flex flex-wrap items-center gap-1.5">
                <Badge variant={STATUS_VARIANT[error.effectiveStatus]} className="shrink-0">
                  {error.effectiveStatus}
                </Badge>
                {regressed && (
                  <Badge variant="outline" className="text-muted-foreground shrink-0 text-[10px]">
                    was {error.status}
                  </Badge>
                )}
                <Badge variant="secondary" className="shrink-0 text-[10px]">
                  {error.origin}
                </Badge>
                <span className="text-destructive font-mono text-xs font-semibold break-words">{error.errorType}</span>
              </span>
              <span className="text-foreground text-xs break-words">{error.message}</span>
            </span>
          </ButtonPrimitive>
        </TableCell>
        <TableCell className={cn(cellClass, 'text-muted-foreground font-mono text-xs break-all')}>
          {error.route}
        </TableCell>
        <TableCell className={cn(cellClass, 'tabular-nums')}>
          <Badge variant="secondary">×{error.count}</Badge>
        </TableCell>
        <TableCell className={cn(cellClass, 'text-muted-foreground text-xs')}>{fmt(error.lastSeen)}</TableCell>
        <TableCell className={cellClass}>
          <StatusSelect
            value={error.status}
            disabled={props.pending}
            onChange={(status) => props.onSetStatus({ fingerprint: error.fingerprint, status })}
          />
        </TableCell>
      </TableRow>
      {open && (
        <TableRow className="bg-muted/20 hover:bg-muted/20">
          <TableCell colSpan={5} className="px-3 pb-3 whitespace-normal">
            <ErrorDetail error={error} />
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  )
}

// The raw-status triage control. Its COLOUR isn't the state signal — that's the effective-status Badge in
// the Error cell (a regressed row reads `active` there while its raw status stays `resolved` here); this is
// purely "what triage state do I want to set".
function StatusSelect(props: { value: ErrorStatus; disabled: boolean; onChange: (status: ErrorStatus) => void }) {
  return (
    <NativeSelect
      size="sm"
      aria-label="Set error status"
      value={props.value}
      disabled={props.disabled}
      onChange={(ev) => props.onChange(ev.target.value as ErrorStatus)}
    >
      {ERROR_STATUSES.map((s) => (
        <NativeSelectOption key={s} value={s}>
          {s}
        </NativeSelectOption>
      ))}
    </NativeSelect>
  )
}

// The expanded issue: the latest occurrence's stack, its breadcrumb trail, and the full wide-event
// attribute bag (envelope + OTel-style dotted keys) teed onto the row.
function ErrorDetail(props: { error: StoredError }) {
  const crumbs = props.error.context?.breadcrumbs ?? []
  return (
    <div className="flex flex-col gap-3 pt-2">
      <StackPanel type={props.error.errorType} message={props.error.message} stack={props.error.stack} />
      {crumbs.length > 0 && <Breadcrumbs entries={crumbs} />}
      <AttributeGrid error={props.error} />
    </div>
  )
}

// A dark, terminal-style panel: the exception's type + message and its full captured stack.
function StackPanel(props: { type: string; message: string; stack: string | null }) {
  return (
    <div className="flex flex-col gap-2 rounded-md bg-neutral-900 p-3">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-mono text-xs font-semibold text-red-300">{props.type}</span>
        <span className="font-mono text-xs break-words text-neutral-200">{props.message}</span>
      </div>
      {props.stack && (
        <pre className="scrollbar overflow-x-auto font-mono text-[11px] leading-relaxed whitespace-pre text-neutral-300">
          {props.stack}
        </pre>
      )}
    </div>
  )
}

function Breadcrumbs(props: { entries: NonNullable<ErrorCtx['breadcrumbs']> }) {
  return (
    <Section title="Breadcrumbs">
      <ol className="flex flex-col gap-1 pt-1">
        {props.entries.map((b) => (
          <li key={`${String(b.t)}-${b.type}-${b.message}`} className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground shrink-0 tabular-nums">{new Date(b.t).toLocaleTimeString()}</span>
            <Badge variant="outline" className="shrink-0 font-mono text-[10px]">
              {b.type}
            </Badge>
            <span className="text-muted-foreground font-mono break-words">{b.message}</span>
          </li>
        ))}
      </ol>
    </Section>
  )
}

// Context keys read fine raw (camelCase); relabel only the ones that don't.
const CONTEXT_LABEL: Record<string, string> = { ip: 'IP address' }

// The wide event, two panes: the stored `errors`-row envelope, and the row's own facts followed by the
// captured context rendered GENERICALLY — the server's buildErrorAttributes stays the single owner of
// the OTel key mapping, so there's no parallel key list here to keep in lockstep. Breadcrumbs get their
// own section above; nulls and non-scalars are dropped so only what was actually captured shows.
function AttributeGrid(props: { error: StoredError }) {
  const error = props.error

  const event: [string, string | null][] = [
    ['fingerprint', error.fingerprint],
    ['signature', error.signature],
    ['occurrences', String(error.count)],
    ['first_seen', fmt(error.firstSeen)],
    ['last_seen', fmt(error.lastSeen)],
    ['status', error.status],
    ['effective_status', error.effectiveStatus],
    ['github_issue', error.githubIssueUrl ? `#${String(error.githubIssueNumber)} — ${error.githubIssueUrl}` : null],
  ]

  const attributes: [string, string | null][] = [
    ['error.origin', error.origin],
    ['url.path', error.route],
    ['exception.type', error.errorType],
    ['exception.message', error.message],
    ...Object.entries(error.context ?? {})
      .filter(([key]) => key !== 'breadcrumbs')
      .map(([key, value]): [string, string | null] => [CONTEXT_LABEL[key] ?? key, attrStr(value)]),
  ]

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Section title="Event">
        {event
          .filter((entry): entry is [string, string] => entry[1] != null)
          .map(([k, v]) => (
            <Row key={k} label={k} value={v} />
          ))}
      </Section>
      <Section title="Attributes">
        {attributes
          .filter((entry): entry is [string, string] => entry[1] != null)
          .map(([k, v]) => (
            <Row key={k} label={k} value={v} />
          ))}
      </Section>
    </div>
  )
}

function Section(props: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-muted-foreground text-[11px] font-semibold tracking-wide uppercase">{props.title}</span>
      <dl className="divide-border flex flex-col divide-y">{props.children}</dl>
    </div>
  )
}

// One attribute row: a mono key over its value. A long/multiline value scrolls in its own block.
function Row(props: { label: string; value: string }) {
  const isLong = props.value.length > 80 || props.value.includes('\n')
  return (
    <div className="flex flex-col gap-0.5 py-1">
      <dt className="text-muted-foreground font-mono text-[11px]">{props.label}</dt>
      {isLong ? (
        <dd>
          <pre className="scrollbar max-h-40 overflow-auto rounded bg-neutral-900 p-2 font-mono text-[11px] whitespace-pre-wrap text-neutral-200">
            {props.value}
          </pre>
        </dd>
      ) : (
        <dd className="text-foreground font-mono text-xs break-all">{props.value}</dd>
      )}
    </div>
  )
}

function ErrorsPanelSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-16 rounded-lg" />
        ))}
      </div>
      <Skeleton className="h-9 w-72" />
      <div className="flex flex-col gap-px overflow-hidden rounded-xl border">
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} className="h-14 rounded-none" />
        ))}
      </div>
    </div>
  )
}

/**
 * Catches the error thrown by the gated query. A `SUPER_ADMIN_ONLY` code means the caller isn't a
 * super-admin; anything else is a genuine failure (e.g. a server 500) — distinguish them so a real
 * error isn't mislabeled as "you lack permission", which sent us on a wrong-turn diagnosis once.
 */
class ErrorsPanelBoundary extends Component<{ children: ReactNode }, { error: unknown }> {
  override state: { error: unknown } = { error: null }
  static getDerivedStateFromError(error: unknown) {
    return { error }
  }
  override render() {
    if (this.state.error != null) {
      const forbidden = (this.state.error as { message?: unknown }).message === 'SUPER_ADMIN_ONLY'
      return (
        <p className="text-sm text-amber-700 dark:text-amber-400">
          {forbidden
            ? 'This section is only available to D’Anchiano super-admins.'
            : 'Couldn’t load captured errors — the request failed. Check the server logs for details.'}
        </p>
      )
    }
    return this.props.children
  }
}
